import { useMutation } from "@tanstack/react-query";

import { ROUTES } from "~/router";
import { useAuthStore } from "~/stores";
import { errorToast } from "~/ui/admin";
import { useRedirect, validateError } from "~/utils";
import { BookingFlow } from "../booking-flows";
import { UseMutationProps } from "../types";
import { mutations } from "./factories";
import {
  AuthenticatedScreeningPayload,
  NewPatientScreeningPayload,
  PatientScreeningFormSchema,
  PublicScreeningPayload,
} from "./types";

const toApiPayload = (
  payload: AuthenticatedScreeningPayload | PublicScreeningPayload,
) =>
  ({
    ...payload,
    state:
      typeof payload.state === "object" ? payload.state?.id : payload.state,
    insurance:
      payload.insurance && typeof payload.insurance === "object"
        ? payload.insurance.id
        : payload.insurance,
    answers: {
      insuranceNotListed: payload.answers.insuranceNotListed ?? null,
      insuranceFreeText: payload.answers.insuranceFreeText ?? null,
    },
  }) as unknown as AuthenticatedScreeningPayload | PublicScreeningPayload;

const mapScreeningFormToApi = (
  form: PatientScreeningFormSchema,
): AuthenticatedScreeningPayload | PublicScreeningPayload => {
  return {
    state: form.state,
    insurance: form.insurance ?? null,
    patientType: form.patientType,
    email: form.email,
    answers: {
      insuranceNotListed:
        "insuranceNotListed" in form.answers
          ? (form.answers.insuranceNotListed ?? null)
          : null,
      insuranceFreeText:
        "insuranceFreeText" in form.answers
          ? (form.answers.insuranceFreeText ?? null)
          : null,
    },
  };
};

export const useSaveScreeningMutation = (
  props?: UseMutationProps<typeof mutations.saveScreening>,
) => {
  const redirect = useRedirect();

  return useMutation({
    mutationFn: mutations.saveScreening,
    ...props,
    onError: (error, variables) => {
      const parsedError = validateError(error);

      if (parsedError?.response.data.error.code === "user_already_registered") {
        if ("email" in variables) {
          return redirect(
            `${ROUTES.fullLogin}?email=${encodeURIComponent(variables.payload.email)}`,
          );
        }
      }

      errorToast();
    },
  });
};

export const useSaveUserScreeningMutation = (
  props?: UseMutationProps<typeof mutations.saveUserScreening>,
) => {
  return useMutation({
    mutationFn: mutations.saveUserScreening,
    ...props,
    onError: errorToast,
  });
};

export const useConditionalScreeningMutation = () => {
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn());

  return useMutation({
    mutationFn: ({
      bookingFlowId,
      formValues,
      joinWaitlist,
    }: {
      bookingFlowId: BookingFlow["id"];
      formValues: PatientScreeningFormSchema;
      joinWaitlist?: boolean;
    }) => {
      const payload = mapScreeningFormToApi(formValues);
      const apiPayload = toApiPayload(payload);

      if (isLoggedIn) {
        return mutations.saveUserScreening({
          bookingFlowId,
          payload: apiPayload as
            NewPatientScreeningPayload | AuthenticatedScreeningPayload,
        });
      }

      return mutations.saveScreening({
        bookingFlowId,
        payload: { ...payload, joinWaitlist },
      });
    },
  });
};
